import { useContext } from 'react';

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { ConfigContext } from '../../../aurr/contexts/ConfigContext';
import LoginContext from '../../../aurr/contexts/LoginContext';
import type { ApiRequestProps } from '../../../common/interface';
import { collectionWithMeta } from '../../../common/schema';
import { CollectionWithMeta } from '../../../common/type';
import { COLLECTION_PAGINATED_ITEMS_KEY } from '../constants';

interface CollectionRequestParams extends ApiRequestProps {
  id: string;
  pathname?: string;
}

export const useCollectionPaginatedItems = ({
  id,
  baseURL,
  params,
}: CollectionRequestParams) => {
  const { COMMUNITY_FUNCTIONS_URL } = useContext(ConfigContext);
  const { userProfile } = useContext(LoginContext);

  const sub = userProfile?.sub;
  const { ...restParams } = params;
  const parsedBaseUrl = baseURL || COMMUNITY_FUNCTIONS_URL;
  const axiosConfig = {
    baseURL: `${parsedBaseUrl}/store/v1/collections/${id}/items`,
  };

  return useQuery<CollectionWithMeta | undefined>({
    queryKey: [
      COLLECTION_PAGINATED_ITEMS_KEY,
      { parsedBaseUrl, id, sub },
      JSON.stringify(params),
    ],
    queryFn: async (): Promise<CollectionWithMeta | undefined> => {
      const api = axios.create(axiosConfig);

      const response = await api
        .get('', { params: { provider: 'aws', ...restParams } })
        .catch(reason => {
          // eslint-disable-next-line no-console
          console.error('Error fetching Collection Items data: ', reason);
          return reason.response;
        });

      const collectionResponse = collectionWithMeta.safeParse(response?.data);
      if (collectionResponse.success) {
        return collectionResponse.data as CollectionWithMeta;
      }
      // eslint-disable-next-line no-console
      console.error(
        'Error parsing Paginated Collection Items data: ',
        collectionResponse.error
      );
      return undefined;
    },
    enabled: !!id,
  });
};
